import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import validate from 'validate.js';

import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import {
	FormField,
	Modal,
	Paragraph,
	RuPrimaryButton,
	Warning,
} from '@silverrailtech/srtlibrary';

import messages from '../../../messagesCustomerAccounts';
import constraints from './createCustomerAccountDialogConstraints';
import {
	CheckboxLabel,
	FormContent,
} from './CreateCustomerAccountDialogStyled';

const CreateCustomerAccountDialog = ({
	intl: { formatMessage },
	isOpen,
	onClose,
	onSubmit,
	initialEmail = '',
	isSubmitting = false,
	error = null,
	tocName = '',
}) => {
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: initialEmail || '',
		confirmEmail: '',
	});

	const [errors, setErrors] = useState({});
	const [fieldValidity, setFieldValidity] = useState({
		firstName: false,
		lastName: false,
		email: false,
		confirmEmail: false,
	});
	const [ageConfirmed, setAgeConfirmed] = useState(false);
	const [marketingConsent, setMarketingConsent] = useState(false);

	useEffect(() => {
		if (isOpen && initialEmail) {
			setFormData((prev) => ({ ...prev, email: initialEmail }));
		}
	}, [isOpen, initialEmail]);

	// Reset form
	useEffect(() => {
		if (!isOpen) {
			setFormData({
				firstName: '',
				lastName: '',
				email: '',
				confirmEmail: '',
			});
			setErrors({});
			setFieldValidity({
				firstName: false,
				lastName: false,
				email: false,
				confirmEmail: false,
			});
			setAgeConfirmed(false);
			setMarketingConsent(false);
		}
	}, [isOpen]);

	const handleChange = (field, value) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();

		const validationErrors = validate(
			formData,
			constraints(formatMessage, messages),
		);

		if (!validate.isEmpty(validationErrors)) {
			setErrors(validationErrors);
			return;
		}

		setErrors({});
		onSubmit(formData);
	};

	return (
		<Modal
			open={isOpen}
			onClose={onClose}
			title={formatMessage(messages.lblCreateAccountDialogTitle)}
			closeButton
			closeButtonText={formatMessage(messages.lblCreateAccountCancel)}
			maxWidth="sm"
		>
			<form onSubmit={handleSubmit} noValidate>
				<FormContent>
					{error && (
						<Alert severity="error" sx={{ marginBottom: '16px' }}>
							{error}
						</Alert>
					)}

					<FormField
						fieldName="firstName"
						label={formatMessage(messages.lblCreateAccountFirstName)}
						fieldValue={formData.firstName}
						onChange={(value) => handleChange('firstName', value)}
						validation="required"
						isFieldValid={fieldValidity.firstName}
						setIsValid={(valid) =>
							setFieldValidity((prev) => ({ ...prev, firstName: valid }))
						}
						isError={!!errors.firstName}
						disabled={isSubmitting}
					/>

					<FormField
						fieldName="lastName"
						label={formatMessage(messages.lblCreateAccountLastName)}
						fieldValue={formData.lastName}
						onChange={(value) => handleChange('lastName', value)}
						validation="required"
						isFieldValid={fieldValidity.lastName}
						setIsValid={(valid) =>
							setFieldValidity((prev) => ({ ...prev, lastName: valid }))
						}
						isError={!!errors.lastName}
						disabled={isSubmitting}
					/>

					<FormField
						fieldName="email"
						label={formatMessage(messages.lblCreateAccountEmail)}
						fieldValue={formData.email}
						onChange={(value) => handleChange('email', value)}
						validation="email"
						isFieldValid={fieldValidity.email}
						setIsValid={(valid) =>
							setFieldValidity((prev) => ({ ...prev, email: valid }))
						}
						isError={!!errors.email}
						disabled={isSubmitting}
					/>

					<FormField
						fieldName="confirmEmail"
						label={formatMessage(messages.lblCreateAccountConfirmEmail)}
						fieldValue={formData.confirmEmail}
						onChange={(value) => handleChange('confirmEmail', value)}
						validation="email"
						isFieldValid={fieldValidity.confirmEmail}
						setIsValid={(valid) =>
							setFieldValidity((prev) => ({
								...prev,
								confirmEmail: valid,
							}))
						}
						isError={!!errors.confirmEmail}
						disabled={isSubmitting}
					/>

					<Warning variant="important" style={{ marginTop: '8px' }}>
						<FormattedMessage {...messages.lblCreateAccountAlertMessage} />
					</Warning>

					<CheckboxLabel
						control={
							<Checkbox
								checked={ageConfirmed}
								onChange={(e) => setAgeConfirmed(e.target.checked)}
								disabled={isSubmitting}
								name="ageConfirmation"
							/>
						}
						label={formatMessage(messages.lblCreateAccountAgeConfirmation)}
						sx={{ marginTop: '16px' }}
					/>

					<CheckboxLabel
						control={
							<Checkbox
								checked={marketingConsent}
								onChange={(e) => setMarketingConsent(e.target.checked)}
								disabled={isSubmitting}
								name="marketingConsent"
							/>
						}
						label={formatMessage(messages.lblCreateAccountMarketingConsent)}
					/>

					<Paragraph fontSize="12px">
						<FormattedMessage
							{...messages.lblCreateAccountTerms}
							values={{
								tocName,
								termsLink: (
									<a
										href={`${window.location.origin}/termsAndConditions`}
										target="_blank"
										rel="noopener noreferrer"
									>
										{formatMessage(messages.lblCreateAccountTermsLink)}
									</a>
								),
							}}
						/>
					</Paragraph>

					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							marginTop: '30px',
						}}
					>
						<RuPrimaryButton
							type="submit"
							isAsync={isSubmitting}
							disabled={isSubmitting || !ageConfirmed}
							aria-label={formatMessage(messages.lblCreateAccountSubmit)}
							style={{ width: '70%' }}
						>
							<FormattedMessage {...messages.lblCreateAccountSubmit} />
						</RuPrimaryButton>
					</div>
				</FormContent>
			</form>
		</Modal>
	);
};

CreateCustomerAccountDialog.propTypes = {
	intl: PropTypes.object.isRequired,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	initialEmail: PropTypes.string,
	isSubmitting: PropTypes.bool,
	error: PropTypes.string,
	tocName: PropTypes.string,
};

export { CreateCustomerAccountDialog as CreateCustomerAccountDialogAlias };
export default injectIntl(CreateCustomerAccountDialog);
